import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Surface, PageSection, formatINR } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Plus, Pencil, Trash2, Building, Save, ArrowLeft, MapPin, ScrollText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const F = ({ label, className = "", children }) => (
  <label className={`text-sm ${className}`}>
    <span className="block text-[11px] uppercase tracking-widest text-gray-500 mb-1">{label}</span>
    {children}
  </label>
);

const EMPTY = {
  name: "", code: "", branch_type: "branch",
  contact_person: "", phone: "", alt_phone: "", email: "",
  address_line1: "", address_line2: "", city: "", district: "", state: "", pincode: "", country: "India",
  latitude: "", longitude: "",
  gstin: "", pan: "",
  price_level: 2,
  opening_balance: 0,
  opening_balance_at: new Date().toISOString().slice(0, 10),
  notes: "",
};

const TYPE_TONE = {
  branch: "bg-emerald-50 text-emerald-800 border-emerald-200",
  franchise: "bg-blue-50 text-blue-800 border-blue-200",
};

export default function SalesBranches() {
  const nav = useNavigate();
  const [view, setView] = useState({ mode: "list" });
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const load = useCallback(async () => {
    try {
      const params = {};
      if (typeFilter) params.branch_type = typeFilter;
      if (search) params.search = search;
      const r = await api.get("/sales/branches", { params });
      setItems(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [typeFilter, search]);
  useEffect(() => { if (view.mode === "list") load(); }, [view.mode, load]);

  const remove = async (b) => {
    if (!window.confirm(`Deactivate "${b.name}"?`)) return;
    try { await api.delete(`/sales/branches/${b.id}`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (view.mode !== "list") return <BranchEditor id={view.id} onBack={() => setView({ mode: "list" })} />;

  return (
    <div className="space-y-4">
      <PageSection title="Branches & Franchises" description="HQ-owned branches (same GSTIN as HQ) and independent franchises (own GSTIN). Used across Sales, Ledgers, and Stock Transfer modules." action={
        <Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" onClick={() => setView({ mode: "new" })} data-testid="sb-new-btn">
          <Plus className="h-4 w-4" /> New Branch / Franchise
        </Button>
      }>
        <Surface padded={false}>
          <div className="p-3 border-b border-gray-100 flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 max-w-sm min-w-[220px]">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / code / city / GSTIN..." className="pl-8" data-testid="sb-search" />
            </div>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="h-9 rounded-md border border-gray-200 text-sm px-2" data-testid="sb-type-filter">
              <option value="">All types</option>
              <option value="branch">Branches</option>
              <option value="franchise">Franchises</option>
            </select>
            <span className="text-xs text-gray-500 ml-auto">{items.length} location(s)</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">City / State</th>
                <th className="px-3 py-2">GSTIN</th>
                <th className="px-3 py-2">Contact</th>
                <th className="px-3 py-2 text-right">Opening Bal.</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-10 text-center text-gray-400">
                  <Building className="h-8 w-8 mx-auto mb-2 opacity-30" /> No branches or franchises yet.
                </td></tr>
              )}
              {items.map((b) => (
                <tr key={b.id} className="hover:bg-gray-50" data-testid={`sb-row-${b.id}`}>
                  <td className="px-3 py-2 font-mono text-xs font-semibold">{b.code}</td>
                  <td className="px-3 py-2 font-medium">{b.name}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] uppercase tracking-widest font-semibold border rounded px-2 py-0.5 ${TYPE_TONE[b.branch_type]}`}>{b.branch_type}</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">{[b.city, b.state].filter(Boolean).join(", ") || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-600">{b.gstin || "—"}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">{b.phone || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs">{b.branch_type === "franchise" ? formatINR(b.opening_balance || 0) : "—"}</td>
                  <td className="px-3 py-2 text-right">
                    {b.branch_type === "franchise" && (
                      <button className="h-7 w-7 rounded hover:bg-blue-50 inline-flex items-center justify-center mr-1" onClick={() => nav(`/sales/franchises/${b.id}/ledger`)} title="Franchise Ledger" data-testid={`sb-ledger-${b.id}`}>
                        <ScrollText className="h-3.5 w-3.5 text-blue-600" />
                      </button>
                    )}
                    <button className="h-7 w-7 rounded hover:bg-gray-100 inline-flex items-center justify-center" onClick={() => setView({ mode: "edit", id: b.id })} data-testid={`sb-edit-${b.id}`}>
                      <Pencil className="h-3.5 w-3.5 text-gray-500" />
                    </button>
                    <button className="h-7 w-7 rounded hover:bg-red-50 inline-flex items-center justify-center ml-1" onClick={() => remove(b)} data-testid={`sb-delete-${b.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Surface>
      </PageSection>
    </div>
  );
}

function BranchEditor({ id, onBack }) {
  const isNew = !id;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);
  const [localities, setLocalities] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!id) return;
    api.get(`/sales/branches/${id}`).then((r) => setForm({ ...EMPTY, ...r.data, opening_balance_at: r.data.opening_balance_at || EMPTY.opening_balance_at }));
  }, [id]);

  const lookupPin = async () => {
    if (!/^\d{6}$/.test(form.pincode || "")) return;
    setPinLoading(true);
    try {
      const r = await api.get(`/sales/pincode/${form.pincode}`);
      setLocalities(r.data.localities || []);
      setForm((f) => ({
        ...f,
        district: r.data.district || f.district,
        state: r.data.state || f.state,
        city: f.city || r.data.localities?.[0]?.name || "",
      }));
      toast.success(`Found ${r.data.localities.length} localit${r.data.localities.length === 1 ? "y" : "ies"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Pincode lookup failed"); }
    finally { setPinLoading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.code.trim()) return toast.error("Code is required");
    if (form.branch_type === "franchise" && !form.gstin.trim()) return toast.error("Franchise must have its own GSTIN");
    setSaving(true);
    try {
      const payload = {
        ...form,
        latitude: form.latitude === "" ? null : Number(form.latitude),
        longitude: form.longitude === "" ? null : Number(form.longitude),
        opening_balance: Number(form.opening_balance) || 0,
      };
      if (isNew) await api.post("/sales/branches", payload);
      else await api.put(`/sales/branches/${id}`, payload);
      toast.success(`${form.branch_type === "branch" ? "Branch" : "Franchise"} saved`);
      onBack();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={onBack} className="gap-1.5" data-testid="sb-back"><ArrowLeft className="h-4 w-4" /> Back</Button>
        <h2 className="font-heading font-bold text-lg text-gray-900">{isNew ? "New Branch / Franchise" : `Edit ${form.name || ""}`}</h2>
        <div className="ml-auto">
          <Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" onClick={save} disabled={saving} data-testid="sb-save">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <Surface>
        <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-gray-100">Basic</h3>
        <div className="grid grid-cols-3 gap-4">
          <F label="Name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="sb-name" /></F>
          <F label="Code *"><Input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} className="font-mono" data-testid="sb-code" /></F>
          <F label="Type *">
            <div className="flex gap-2">
              {["branch", "franchise"].map((t) => (
                <button
                  key={t}
                  onClick={() => set("branch_type", t)}
                  className={`flex-1 h-9 rounded-md border text-sm font-semibold ${form.branch_type === t ? "border-[#9B1C1C] bg-[#9B1C1C]/5 text-[#9B1C1C]" : "border-gray-200 hover:bg-gray-50"}`}
                  data-testid={`sb-type-${t}`}
                >
                  {t === "branch" ? "Branch (same GST)" : "Franchise (own GST)"}
                </button>
              ))}
            </div>
          </F>
          <F label="Contact Person"><Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} data-testid="sb-contact" /></F>
          <F label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="sb-phone" /></F>
          <F label="Alt. Phone"><Input value={form.alt_phone} onChange={(e) => set("alt_phone", e.target.value)} data-testid="sb-alt-phone" /></F>
          <F label="Email" className="col-span-2"><Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="sb-email" /></F>
          <F label="Price Level">
            <select value={form.price_level} onChange={(e) => set("price_level", Number(e.target.value))} className="h-9 w-full rounded-md border border-gray-200 text-sm px-2" data-testid="sb-price-level">
              <option value={1}>Level 1 (MRP)</option>
              <option value={2}>Level 2 (Retail)</option>
              <option value={3}>Level 3 (Wholesale)</option>
              <option value={4}>Level 4 (Special)</option>
            </select>
          </F>
        </div>
      </Surface>

      <Surface>
        <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-gray-100 flex items-center gap-2">
          <MapPin className="h-4 w-4 text-gray-500" /> Address
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <F label="Address Line 1" className="col-span-3"><Input value={form.address_line1} onChange={(e) => set("address_line1", e.target.value)} data-testid="sb-addr1" /></F>
          <F label="Address Line 2" className="col-span-3"><Input value={form.address_line2} onChange={(e) => set("address_line2", e.target.value)} data-testid="sb-addr2" /></F>
          <F label="Pincode">
            <div className="flex gap-2">
              <Input value={form.pincode} onChange={(e) => set("pincode", e.target.value.replace(/[^\d]/g, "").slice(0, 6))} onBlur={lookupPin} className="font-mono" data-testid="sb-pincode" />
              <Button variant="outline" size="sm" onClick={lookupPin} disabled={pinLoading || form.pincode?.length !== 6} data-testid="sb-pincode-lookup">
                {pinLoading ? "..." : "Fetch"}
              </Button>
            </div>
          </F>
          <F label="City">
            {localities.length > 1 ? (
              <select value={form.city} onChange={(e) => set("city", e.target.value)} className="h-9 w-full rounded-md border border-gray-200 text-sm px-2" data-testid="sb-city-select">
                <option value="">Pick from localities…</option>
                {localities.map((l) => <option key={l.name} value={l.name}>{l.name}</option>)}
                <option value="__custom">Other (type below)</option>
              </select>
            ) : (
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="sb-city" />
            )}
            {form.city === "__custom" && <Input value={""} onChange={(e) => set("city", e.target.value)} placeholder="Type city name" className="mt-1" />}
          </F>
          <F label="District"><Input value={form.district} onChange={(e) => set("district", e.target.value)} data-testid="sb-district" /></F>
          <F label="State"><Input value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="sb-state" /></F>
          <F label="Latitude"><Input type="number" step="any" value={form.latitude} onChange={(e) => set("latitude", e.target.value)} data-testid="sb-lat" /></F>
          <F label="Longitude"><Input type="number" step="any" value={form.longitude} onChange={(e) => set("longitude", e.target.value)} data-testid="sb-lng" /></F>
        </div>
      </Surface>

      <Surface>
        <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-gray-100">Statutory {form.branch_type === "branch" && <span className="text-[11px] text-gray-500 font-normal ml-2">(branch shares HQ GST — enter only if different)</span>}</h3>
        <div className="grid grid-cols-3 gap-4">
          <F label={`GSTIN ${form.branch_type === "franchise" ? "*" : ""}`}><Input value={form.gstin} onChange={(e) => set("gstin", e.target.value.toUpperCase())} className="font-mono" data-testid="sb-gstin" /></F>
          <F label="PAN"><Input value={form.pan} onChange={(e) => set("pan", e.target.value.toUpperCase())} className="font-mono" data-testid="sb-pan" /></F>
        </div>
      </Surface>

      {form.branch_type === "franchise" && (
        <Surface>
          <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-gray-100">Opening Ledger Balance</h3>
          <div className="grid grid-cols-3 gap-4">
            <F label="Amount (₹)">
              <Input type="number" step="0.01" value={form.opening_balance} onChange={(e) => set("opening_balance", e.target.value)} className="text-right tabular-nums" data-testid="sb-opening-bal" />
              <span className="text-[10px] text-gray-500 block mt-1">Positive = franchise owes HQ. Negative = advance paid.</span>
            </F>
            <F label="As-of Date"><Input type="date" value={form.opening_balance_at} onChange={(e) => set("opening_balance_at", e.target.value)} data-testid="sb-opening-bal-at" /></F>
          </div>
        </Surface>
      )}

      <Surface>
        <F label="Notes"><Input value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="sb-notes" /></F>
      </Surface>
    </div>
  );
}
